// Static SA2 choropleth rendered as inline SVG (NOT maplibre/WebGL).
// A report is a fixed, rasterised page, so we draw polygons directly: html2canvas
// captures SVG natively, sidestepping the WebGL-canvas capture problem entirely.
//
// features:         geojson features for the electorate (properties.area_id + geometry, lng/lat).
// fillFor/strokeFor: per-SA2 colour / emphasis.
// hatchFor:         optional (area_id) => { line, bg } | null. When set, that SA2 is filled with
//                   `bg` and overlaid with diagonal-slash lines in `line`. Implemented as explicit
//                   <line> primitives clipped to the polygon — NOT an SVG <pattern> (patterns are
//                   flaky in html2canvas); lines/paths always rasterise, so the hatch survives export.
// basemapFeatures:  optional faint-grey context layer drawn behind (same-origin vector, capture-safe).
import React, { useMemo } from "react";

const polysOf = (geom) => {
  if (!geom) return [];
  if (geom.type === "Polygon") return [geom.coordinates];
  if (geom.type === "MultiPolygon") return geom.coordinates;
  return [];
};

// 45° slash lines covering a projected bbox (clipped to the polygon at render time).
const hatchLines = ([x0, y0, x1, y1], spacing = 3.4) => {
  const lines = [];
  const h = y1 - y0;
  for (let o = 0; o <= (x1 - x0) + h; o += spacing) {
    lines.push([+(x0 + o).toFixed(1), +y0.toFixed(1), +(x0 + o - h).toFixed(1), +y1.toFixed(1)]);
  }
  return lines;
};

export default function SVGChoropleth({
  features = [],
  basemapFeatures = [],
  fillFor,
  strokeFor,
  hatchFor,
  idPrefix = "sc",
  width = 340,
  height = 200,
  bg = "transparent",
  basemapFill = "#e9edf2",
  basemapStroke = "#dfe4ea",
}) {
  const { paths, basePaths, ok } = useMemo(() => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    features.forEach((f) =>
      polysOf(f.geometry).forEach((poly) =>
        poly.forEach((ring) =>
          ring.forEach(([x, y]) => {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          })
        )
      )
    );
    if (!Number.isFinite(minX) || maxX === minX || maxY === minY) {
      return { paths: [], basePaths: [], ok: false };
    }
    const s = Math.min(width / (maxX - minX), height / (maxY - minY)) * 0.96;
    const offX = (width - (maxX - minX) * s) / 2;
    const offY = (height - (maxY - minY) * s) / 2;
    const project = ([x, y]) => [offX + (x - minX) * s, height - offY - (y - minY) * s];
    const toPath = (geom) =>
      polysOf(geom)
        .map((poly) => poly.map((ring) => "M" + ring.map(project).map((p) => p.map((n) => n.toFixed(1)).join(",")).join("L") + "Z").join(""))
        .join("");

    const inView = (geom) => {
      let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
      polysOf(geom).forEach((poly) => poly.forEach((ring) => ring.forEach(([x, y]) => {
        if (x < bx0) bx0 = x; if (y < by0) by0 = y; if (x > bx1) bx1 = x; if (y > by1) by1 = y;
      })));
      const padX = (maxX - minX) * 0.6, padY = (maxY - minY) * 0.6;
      return bx1 >= minX - padX && bx0 <= maxX + padX && by1 >= minY - padY && by0 <= maxY + padY;
    };
    const basePaths = basemapFeatures.filter((f) => inView(f.geometry)).map((f) => toPath(f.geometry)).filter(Boolean);

    const paths = features.map((f) => {
      const id = String(f.properties?.area_id ?? "");
      // project + track this feature's screen bbox (for hatch line extent)
      let px0 = Infinity, py0 = Infinity, px1 = -Infinity, py1 = -Infinity;
      const d = polysOf(f.geometry)
        .map((poly) => poly.map((ring) => {
          const pts = ring.map(project);
          pts.forEach(([x, y]) => { if (x < px0) px0 = x; if (y < py0) py0 = y; if (x > px1) px1 = x; if (y > py1) py1 = y; });
          return "M" + pts.map((p) => p.map((n) => n.toFixed(1)).join(",")).join("L") + "Z";
        }).join(""))
        .join("");
      const hatch = hatchFor?.(id) || null;
      const stroke = strokeFor?.(id);
      return {
        id, d,
        fill: hatch ? hatch.bg : (fillFor?.(id) || "#eef2f7"),
        stroke: stroke?.color || "#ffffff",
        strokeWidth: stroke?.width ?? 0.5,
        hatch: hatch ? { color: hatch.line, box: [px0, py0, px1, py1] } : null,
      };
    });
    return { paths, basePaths, ok: true };
  }, [features, basemapFeatures, fillFor, strokeFor, hatchFor, width, height]);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ background: bg, display: "block" }}>
      <defs>
        {ok && paths.map((p, i) => p.hatch ? (
          <clipPath key={`c${i}`} id={`${idPrefix}-hc-${i}`}><path d={p.d} /></clipPath>
        ) : null)}
      </defs>
      {ok && basePaths.map((d, i) => (
        <path key={`b${i}`} d={d} fill={basemapFill} stroke={basemapStroke} strokeWidth={0.4} />
      ))}
      {ok && paths.map((p, i) => (
        <path key={p.id || i} d={p.d} fill={p.fill} stroke={p.stroke} strokeWidth={p.strokeWidth} />
      ))}
      {ok && paths.map((p, i) => p.hatch ? (
        <g key={`h${i}`} clipPath={`url(#${idPrefix}-hc-${i})`}>
          {hatchLines(p.hatch.box).map((ln, j) => (
            <line key={j} x1={ln[0]} y1={ln[1]} x2={ln[2]} y2={ln[3]} stroke={p.hatch.color} strokeWidth={0.9} strokeLinecap="round" />
          ))}
        </g>
      ) : null)}
    </svg>
  );
}
